'use client';

import Link from 'next/link';
import { BarChart3, Database, MessageSquare, LayoutDashboard, Zap } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-blue-500" />
            <span className="text-xl font-bold">VizionAnalytics</span>
          </div>
          <div className="flex gap-4">
            <Link
              href="/login"
              className="px-4 py-2 text-zinc-400 hover:text-white transition"
            >
              Log in
            </Link>
            <Link
              href="/signup"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 py-24 text-center">
        <h1 className="text-5xl font-bold mb-6">
          AI-Powered Database Analytics
        </h1>
        <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-10">
          Connect your database, ask questions in plain English, and build beautiful dashboards.
          No SQL knowledge required.
        </p>
        <div className="flex gap-4 justify-center">
          <Link
            href="/signup"
            className="px-8 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-lg font-medium transition"
          >
            Start Free
          </Link>
          <Link
            href="#features"
            className="px-8 py-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-lg font-medium transition"
          >
            Learn More
          </Link>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 py-24">
        <h2 className="text-3xl font-bold text-center mb-16">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard
            icon={<Database className="h-10 w-10" />}
            title="Connect Your Database"
            description="Securely connect PostgreSQL, MySQL, or any SQL database in seconds. We auto-discover your schema."
          />
          <FeatureCard
            icon={<MessageSquare className="h-10 w-10" />}
            title="Ask Questions"
            description="Chat with your data using natural language. Our AI translates your questions into SQL instantly."
          />
          <FeatureCard
            icon={<LayoutDashboard className="h-10 w-10" />}
            title="Build Dashboards"
            description="Save your favorite queries and visualizations. Build shareable dashboards with charts and KPIs."
          />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-900 py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Zap className="h-12 w-12 text-blue-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Ready to explore your data?</h2>
          <p className="text-zinc-400 mb-8">
            Get started in under 2 minutes. No credit card required.
          </p>
          <Link
            href="/signup"
            className="px-8 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-lg font-medium transition inline-block"
          >
            Create Free Account
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center text-zinc-500 text-sm">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            <span>VizionAnalytics</span>
          </div>
          <div>© 2026 VizionTools. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="p-6 bg-zinc-900 rounded-xl border border-zinc-800">
      <div className="text-blue-500 mb-4">{icon}</div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-zinc-400">{description}</p>
    </div>
  );
}
